import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import Layout from "../components/Layout";
import StatusBadge from "../components/StatusBadge";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RefreshCw, Link2, CheckCircle2, AlertTriangle, Loader2, Shield, X } from "lucide-react";
import { toast, Toaster } from "sonner";
import { formatDateTime } from "../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";

export default function Sync() {
  const { user, refresh } = useAuth();
  const [sheetInput, setSheetInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState(null);
  const [myLogs, setMyLogs] = useState([]);
  const [changing, setChanging] = useState(false);
  const [dupDialog, setDupDialog] = useState(null); // { duplicates: [...] }

  const loadLogs = async () => {
    try {
      const { data } = await api.get("/sync/my-logs");
      setMyLogs(data);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => { loadLogs(); }, []);

  const connectSheet = async (e) => {
    e.preventDefault();
    if (user?.sheet_id) {
      const ok = window.confirm("This will replace your currently linked sheet. Future syncs will use the new sheet only. Continue?");
      if (!ok) return;
    }
    setBusy(true);
    try {
      await api.post("/sync/connect", { sheet_url_or_id: sheetInput });
      toast.success("Sheet connected");
      await refresh();
      setSheetInput("");
      setChanging(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const linkGoogle = async () => {
    const { data } = await api.get("/auth/google/url", { params: { link_mode: true } });
    window.location.href = data.url;
  };

  const doSync = async (skipDuplicates = false) => {
    setSyncing(true);
    setResult(null);
    setDupDialog(null);
    try {
      const { data } = await api.post("/sync/run", null, { params: { skip_duplicates: skipDuplicates } });
      setResult(data);
      toast.success(`Sync complete: +${data.added} added, ${data.updated} updated, ${data.rejected_count} rejected`);
      loadLogs();
      await refresh();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail && detail.code === "duplicates_found") {
        setDupDialog({ duplicates: detail.duplicates || [], message: detail.message });
        return;
      }
      toast.error(formatApiErrorDetail(detail));
    } finally {
      setSyncing(false);
    }
  };

  const runSync = () => doSync(false);
  const skipAndSync = () => doSync(true);

  return (
    <Layout>
      <Toaster richColors position="top-right" />
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold text-slate-900">Sync Google Sheet</h1>
        <p className="text-slate-500 mt-1">Connect your sheet, then click Sync to push candidates into the consolidated database.</p>
      </div>

      {!user?.google_linked && (
        <Card className="p-5 border-amber-200 bg-amber-50/50 mb-6" data-testid="link-google-banner">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <div className="font-semibold text-amber-900">Link your Google account to enable sync</div>
                <p className="text-sm text-amber-800 mt-0.5">We need read-only access to your Google Sheets to import candidates.</p>
              </div>
            </div>
            <Button data-testid="link-google-btn" onClick={linkGoogle} className="bg-amber-600 hover:bg-amber-700">
              <Link2 className="w-4 h-4 mr-1.5" />Link Google
            </Button>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card className="lg:col-span-2 p-6 border-slate-200">
          <h3 className="font-display font-semibold text-lg text-slate-900 mb-1">Connected Google Sheet</h3>
          {user?.sheet_id ? (
            <>
              <p className="text-xs text-slate-500 mb-3">Your sheet is connected. Click <strong>Sync now</strong> on the right to import the latest rows.</p>
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-md flex items-center justify-between gap-2" data-testid="current-sheet">
                <div className="flex items-center gap-2 min-w-0">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  <a href={`https://docs.google.com/spreadsheets/d/${user.sheet_id}`} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-700 hover:underline truncate font-mono">
                    {user.sheet_id}
                  </a>
                </div>
                <button
                  type="button"
                  data-testid="change-sheet-btn"
                  onClick={() => { setSheetInput(""); setChanging(true); }}
                  className="text-xs text-slate-500 hover:text-indigo-700 underline flex-shrink-0"
                >
                  Link another sheet
                </button>
              </div>
              {changing && (
                <form onSubmit={connectSheet} className="flex gap-2 mt-3 animate-fade-up">
                  <Input
                    data-testid="sheet-input"
                    placeholder="Paste new Google Sheet URL or ID — this will replace the current sheet"
                    value={sheetInput}
                    onChange={(e) => setSheetInput(e.target.value)}
                    className="h-10"
                    autoFocus
                  />
                  <Button data-testid="connect-sheet-btn" type="submit" disabled={busy || !sheetInput} className="h-10 bg-indigo-600 hover:bg-indigo-700">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Connect"}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => { setChanging(false); setSheetInput(""); }} className="h-10">Cancel</Button>
                </form>
              )}
            </>
          ) : (
            <>
              <p className="text-xs text-slate-500 mb-4">Paste your Google Sheet share link or ID. You only need to do this once — afterwards just click Sync.</p>
              <form onSubmit={connectSheet} className="flex gap-2">
                <Input
                  data-testid="sheet-input"
                  placeholder="https://docs.google.com/spreadsheets/d/..."
                  value={sheetInput}
                  onChange={(e) => setSheetInput(e.target.value)}
                  className="h-11"
                />
                <Button data-testid="connect-sheet-btn" type="submit" disabled={busy || !sheetInput} className="h-11 bg-indigo-600 hover:bg-indigo-700">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Connect"}
                </Button>
              </form>
            </>
          )}
        </Card>

        <Card className="p-6 border-slate-200 flex flex-col">
          <h3 className="font-display font-semibold text-lg text-slate-900 mb-1">Run sync</h3>
          <p className="text-xs text-slate-500 mb-4 flex-1">Imports new rows and updates changed updatable fields. Locked fields stay locked.</p>
          <Button
            data-testid="run-sync-btn"
            onClick={runSync}
            disabled={syncing || !user?.google_linked || !user?.sheet_id}
            className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            {syncing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Syncing…</> : <><RefreshCw className="w-4 h-4 mr-2" />Sync now</>}
          </Button>
          {!user?.sheet_id && <p className="text-xs text-slate-400 mt-2">Connect a sheet first.</p>}
        </Card>
      </div>

      {result && (
        <Card className="p-6 border-slate-200 mb-6 animate-fade-up" data-testid="sync-result">
          <h3 className="font-display font-semibold text-lg text-slate-900 mb-4">Sync summary</h3>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-100">
              <div className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">Added</div>
              <div className="font-display text-3xl font-bold text-emerald-700 mt-1">{result.added}</div>
            </div>
            <div className="p-4 rounded-lg bg-indigo-50 border border-indigo-100">
              <div className="text-xs uppercase tracking-wider text-indigo-700 font-semibold">Updated</div>
              <div className="font-display text-3xl font-bold text-indigo-700 mt-1">{result.updated}</div>
            </div>
            <div className="p-4 rounded-lg bg-amber-50 border border-amber-100">
              <div className="text-xs uppercase tracking-wider text-amber-700 font-semibold">Rejected</div>
              <div className="font-display text-3xl font-bold text-amber-700 mt-1">{result.rejected_count}</div>
            </div>
          </div>
          {result.rejected?.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-slate-900 mb-2">Rejected duplicates</h4>
              <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-64 overflow-y-auto scrollbar-thin">
                {result.rejected.map((r, i) => (
                  <div key={i} className="px-3 py-2 text-sm flex justify-between" data-testid={`rejected-${i}`}>
                    <span className="text-slate-800">Row {r.row} · {r.name} · {r.contact}</span>
                    <span className="text-xs text-amber-600">{r.reason || "duplicate"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {result.errors?.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-semibold text-red-700 mb-2">Errors</h4>
              <div className="text-xs text-red-600 space-y-1">{result.errors.map((e, i) => <div key={i}>{e}</div>)}</div>
            </div>
          )}
        </Card>
      )}

      <Card className="p-6 border-slate-200">
        <h3 className="font-display font-semibold text-lg text-slate-900 mb-4">My recent syncs</h3>
        {myLogs.length === 0 && <p className="text-sm text-slate-400">No syncs yet.</p>}
        <div className="divide-y divide-slate-100">
          {myLogs.map((s) => (
            <div key={s.id} className="py-3 flex items-center justify-between text-sm">
              <div className="text-xs text-slate-500">{formatDateTime(s.ts)}</div>
              <div className="flex gap-4 text-xs">
                <span className="text-emerald-600 font-medium">+{s.added}</span>
                <span className="text-indigo-600 font-medium">{s.updated} updated</span>
                <span className="text-amber-600 font-medium">{s.rejected_count} rejected</span>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-5 border-slate-200 mt-6 bg-slate-50/50">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-slate-500 mt-0.5" />
          <div className="text-xs text-slate-600 leading-relaxed">
            <strong className="text-slate-900">Sheet column order (A→U):</strong> Candidate Name · Contact Number · Email · Current City · Home City · Industry · Industry (other) · Domain · Domain (other) · Work Experience · Experience (other) · Key Skills · Preferred Work Setup · Notice Period · Highest Qualification · Current CTC (LPA) · Expected CTC (LPA) · Candidate Status · Latest Note · Next Follow Up · Resume Link
          </div>
        </div>
      </Card>
      {/* Duplicate detection dialog */}
      <Dialog open={!!dupDialog} onOpenChange={(o) => !o && setDupDialog(null)}>
        <DialogContent data-testid="dup-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Duplicates detected</DialogTitle>
            <DialogDescription>
              {dupDialog?.message || `${dupDialog?.duplicates?.length || 0} duplicate candidate(s) found. Resolve before syncing.`}
            </DialogDescription>
          </DialogHeader>
          {dupDialog?.duplicates?.length > 0 && (
            <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-md" data-testid="dup-list">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider">
                  <tr>
                    <th className="text-left py-2 px-3 font-semibold">Row</th>
                    <th className="text-left py-2 px-3 font-semibold">Name</th>
                    <th className="text-left py-2 px-3 font-semibold">Contact</th>
                    <th className="text-left py-2 px-3 font-semibold">Email</th>
                    <th className="text-left py-2 px-3 font-semibold">Already logged by</th>
                  </tr>
                </thead>
                <tbody>
                  {dupDialog.duplicates.map((d) => (
                    <tr key={`${d.row}-${d.contact}`} className="border-t border-slate-100" data-testid={`dup-row-${d.row}`}>
                      <td className="py-1.5 px-3">{d.row}</td>
                      <td className="py-1.5 px-3 font-medium text-slate-900">{d.name}</td>
                      <td className="py-1.5 px-3 text-slate-600">{d.contact}</td>
                      <td className="py-1.5 px-3 text-slate-600">{d.email}</td>
                      <td className="py-1.5 px-3 text-slate-600">{d.logged_by}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter className="flex sm:justify-between gap-2">
            <Button data-testid="dup-review-btn" variant="outline" onClick={() => setDupDialog(null)}>
              Review Duplicates
            </Button>
            <Button data-testid="dup-skip-btn" onClick={skipAndSync} className="bg-indigo-600 hover:bg-indigo-700">
              Skip Duplicates &amp; Sync
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
